import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  IconButton, Tooltip, Paper,
} from '@mui/material';
import { Add, CheckCircle, Cancel, Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColor: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  compliant: 'success', non_compliant: 'error', pending: 'default',
};

const FortificationList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await millingApi.getFortificationRecords(statusFilter ? { status: statusFilter } : {});
      setRows(res.data ?? res);
    } catch { setError('Failed to load records'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleVerify = async (id: string, compliant: boolean) => {
    const by = window.prompt(`Mark as ${compliant ? 'compliant' : 'non-compliant'}. Verified by:`);
    if (by === null) return;
    try { await millingApi.verifyFortification(id, by, compliant); load(); }
    catch { setError('Verify failed'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this record?')) return;
    try { await millingApi.deleteFortificationRecord(id); load(); }
    catch { setError('Delete failed'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Fortification Records</Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="compliant">Compliant</MenuItem>
            <MenuItem value="non_compliant">Non-Compliant</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/milling/fortification/new')}>
          New Record
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Record #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Order #</TableCell>
                <TableCell align="right">Batch (MT)</TableCell>
                <TableCell>Premix Type</TableCell>
                <TableCell align="right">Target (g/MT)</TableCell>
                <TableCell align="right">Actual (g/MT)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verified By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center">No records found</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.recordNumber}</TableCell>
                  <TableCell>{r.batchDate}</TableCell>
                  <TableCell>{r.productName || '-'}</TableCell>
                  <TableCell>{r.orderNumber || '-'}</TableCell>
                  <TableCell align="right">{r.batchQtyMt ?? '-'}</TableCell>
                  <TableCell>{r.premixType || '-'}</TableCell>
                  <TableCell align="right">{r.targetAdditionRateGPerMt ?? '-'}</TableCell>
                  <TableCell align="right">{r.actualAdditionRateGPerMt ?? '-'}</TableCell>
                  <TableCell>
                    <Chip label={r.status?.replace('_', ' ')} size="small" color={statusColor[r.status] ?? 'default'} />
                  </TableCell>
                  <TableCell>{r.verifiedBy || '-'}</TableCell>
                  <TableCell>
                    {r.status === 'pending' && (
                      <>
                        <Tooltip title="Mark Compliant">
                          <IconButton size="small" color="success" onClick={() => handleVerify(r.id, true)}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark Non-Compliant">
                          <IconButton size="small" color="error" onClick={() => handleVerify(r.id, false)}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => navigate(`/milling/fortification/${r.id}/edit`)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default FortificationList;
